import { buildTabUrl as buildTabUrlCore, composeBuyerUrl as composeBuyerUrlCore } from './core/compose-url';
import { normalizePresendMessage as normalizePresendMessageCore } from './core/send-card-body';
import { clearAllListeners, emit } from './events';
import { sendToWidgetIframe, startBridge } from './launcher/bridge';
import { triggerReset } from './lifecycle';
import { buildQuery, buildQueryWithToken } from './query';
import { isReadyNow, ready as readyPromise, resetReady, signalReady } from './ready';
import { _setApiBase, buildClientMsgId, emitPresendStart, normalizePresendMessage, preSendCard } from './send-card';
import type { DajiCSBootOptions, OpenOptions } from './types';
import { SDK_VERSION } from './version';

const WINDOW_NAME = 'DJ_Chat_Window';

let config: DajiCSBootOptions | null = null;
let openedWindow: Window | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function log(...args: unknown[]): void {
  if (config?.debug) console.log('[DajiCS]', ...args);
}

function warn(...args: unknown[]): void {
  console.warn('[DajiCS]', ...args);
}

function stopPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  openedWindow = null;
}

function startPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  pollTimer = setInterval(() => {
    if (openedWindow && openedWindow.closed) {
      log('detected window closed');
      emit('window:close', { key: WINDOW_NAME });
      stopPolling();
    }
  }, 1000);
}

export function boot(options: DajiCSBootOptions): void {
  if (!options?.baseUrl) throw new Error('DajiCS.boot: baseUrl required');
  if (!options?.apiBase) throw new Error('DajiCS.boot: apiBase required');
  if (!options?.tenant) throw new Error('DajiCS.boot: tenant required');

  if (config) {
    const drift = pickDrift(config, options);
    if (drift.length) {
      warn(
        `boot() called again with different config (${drift.join(', ')}) — ignoring. ` +
          `Call DajiCS.reset() first to re-boot, or DajiCS.update({ tenant, domain }) for runtime swap.`,
      );
      return;
    }
    log('boot() called again with identical config — no-op.');
    return;
  }
  config = { ...options, version: options.version ?? SDK_VERSION };
  log('booted', config);

  _setApiBase(config.apiBase, config.debug ?? false);

  startBridge(config.baseUrl, config.allowedOrigins, {
    autoClose: config.autoCloseOnEnd,
    closeDelay: config.endCloseDelay,
  });
  signalReady();
  emit('ready', undefined);
}

/** 找出两次 boot 中不一致的运行时关键字段；只关心可能导致请求/路由错乱的字段 */
function pickDrift(prev: DajiCSBootOptions, next: DajiCSBootOptions): string[] {
  const keys: (keyof DajiCSBootOptions)[] = ['baseUrl', 'apiBase', 'tenant', 'domain', 'source'];
  return keys.filter((k) => prev[k] !== next[k]);
}

/** 动态更新租户/域名/来源，无需 reset + boot */
export function update(options: Pick<DajiCSBootOptions, 'tenant' | 'domain' | 'source'>): void {
  if (!config) throw new Error('DajiCS: call boot() before update()');
  config = { ...config, ...options };
  sendToWidgetIframe('daji:identity', { tenant: options.tenant, domain: options.domain, source: options.source });
  log('updated', options);
}

/** 返回当前 boot 配置的只读副本，未 boot 时返回 null。调试/排障用。 */
export function getConfig(): DajiCSBootOptions | null {
  return config ? { ...config } : null;
}

export function reset(options?: { clearListeners?: boolean }): void {
  config = null;
  stopPolling();
  resetReady();
  triggerReset();
  if (options?.clearListeners) clearAllListeners();
}

function ensureBooted(): DajiCSBootOptions {
  if (!config) throw new Error('DajiCS: call boot() before open()');
  return config;
}

/** 调 chat/open 拿 sessionId（即融云 groupId） */
async function openWithSession(cfg: DajiCSBootOptions, opts: OpenOptions): Promise<string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(cfg.headers ?? {}),
    ...(opts.headers ?? {}),
  };
  if (opts.token) {
    headers.accesstoken = String(opts.token);
    headers.authorization = String(opts.token);
  }
  if (cfg.tenant) headers['x-tenant-code'] = String(cfg.tenant);
  headers['x-auth-source'] = cfg.source || 'daji';

  const customerType = opts.customerType ?? 1;
  if (customerType === 2 && !opts.userId) {
    throw new Error('DajiCS: 商家客服模式下 userId 为必填项');
  }
  const body: Record<string, unknown> = {
    customerType,
    tenant: cfg.tenant,
    domain: cfg.domain,
  };
  if (opts.userId) body.userId = String(opts.userId);

  const res = await fetch(`${cfg.apiBase}/api/customer/front/chat/open`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`获取会话失败: ${res.status} ${res.statusText}`);
  const data = await res.json();
  if (data.code !== undefined && data.code !== 200) {
    throw new Error(data.msg || data.message || `接口返回错误: code=${data.code}`);
  }
  if (!data.data || typeof data.data !== 'string') {
    throw new Error('接口返回格式错误: missing or invalid sessionId');
  }
  return data.data;
}

/** 合并 boot 全局 extra 与单次 opts.extra（open 覆盖 boot），返回新 OpenOptions */
function withMergedExtra(cfg: DajiCSBootOptions, opts: OpenOptions): OpenOptions {
  if (!cfg.extra && !opts.extra) return opts;
  return { ...opts, extra: { ...(cfg.extra ?? {}), ...(opts.extra ?? {}) } };
}

/** 合并 boot 全局 headers 与单次 opts.headers（open 覆盖 boot） */
function mergedHeaders(cfg: DajiCSBootOptions, opts: OpenOptions): Record<string, string> | undefined {
  if (!cfg.headers && !opts.headers) return undefined;
  return { ...(cfg.headers ?? {}), ...(opts.headers ?? {}) };
}

/** H5 端：用 location.host 注入 host，并保留 ios=OPEN_INNER_PAGE 标识 */
function composeBuyerUrl(cfg: DajiCSBootOptions, opts: OpenOptions, idSegment: string): string {
  const host = typeof location !== 'undefined' ? location.host : '';
  return composeBuyerUrlCore(cfg, withMergedExtra(cfg, opts), idSegment, host);
}

function buildTabUrl(cfg: DajiCSBootOptions, opts: OpenOptions): string {
  const host = typeof location !== 'undefined' ? location.host : '';
  return buildTabUrlCore(cfg, withMergedExtra(cfg, opts), host);
}

/** 公共：根据当前 boot 配置构造访客 URL（供 launcher iframe 模式使用） */
export function buildChatUrl(opts: OpenOptions): string {
  return buildTabUrl(ensureBooted(), opts);
}

/**
 * 构建会话 URL（调用接口获取 sessionId，可选预投卡片）。
 *
 * 与 `open()` 区别：
 * - 不打开窗口、不启轮询、不发出 presend:* 事件
 * - 预投失败不阻塞 URL 返回（仅 warn 日志）
 * - 用于 iframe 模式、小程序 web-view 等无需开新窗口的场景
 *
 * message 参数向后兼容：无 message 时行为与之前完全一致。
 */
export async function buildSessionUrl(opts: OpenOptions): Promise<string> {
  const cfg = ensureBooted();
  if (!opts.token) throw new Error('DajiCS.buildSessionUrl: token is required');
  const sessionId = await openWithSession(cfg, opts);

  if (opts.message) {
    try {
      const presend = normalizePresendMessageCore(opts);
      if (presend) {
        await preSendCard({
          apiBase: cfg.apiBase,
          groupId: sessionId,
          customType: presend.kind,
          card: presend.data,
          token: opts.token,
          tenant: cfg.tenant,
          source: cfg.source,
          debug: cfg.debug,
          extraHeaders: mergedHeaders(cfg, opts),
        });
      }
    } catch (e) {
      warn('pre-send card in buildSessionUrl failed, returning URL anyway:', e);
    }
  }

  return composeBuyerUrl(cfg, opts, sessionId);
}

/** 打开/复用客服窗口（同名 WINDOW_NAME 自动复用） */
function openTab(url: string): Window | null {
  if (typeof window === 'undefined') return null;
  const isReuse = openedWindow != null && !openedWindow.closed;
  const win = window.open(url, WINDOW_NAME);
  if (!win) {
    emit('error', { source: 'window.open', error: new Error('window.open returned null (popup blocked?)') });
    return null;
  }
  openedWindow = win;
  if (isReuse) {
    log('reused existing window, emitting window:focus');
    emit('window:focus', { key: WINDOW_NAME, url });
  }
  startPolling();
  return win;
}

/** iOS 用户代理检测（排除自有 App，其 WKWebView 原生处理 window.open） */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod|iOS/i.test(ua) && !/ChinaMarketApp|DajiBusiness/.test(ua);
}

/**
 * Safari 检测（含桌面 Safari）。
 * Chrome/Edge/Opera/Android WebView 的 UA 也含 "Safari"，必须排除，避免误伤 Chromium 系。
 */
function isSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|Android/i.test(ua);
}

/** Firefox 检测（桌面 + iOS FxiOS） */
function isFirefox(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Firefox|FxiOS/i.test(navigator.userAgent);
}

/**
 * popup 策略较严、async 后 window.open 易被拦的浏览器。
 * 仅收窄到 iOS / Safari / Firefox，不扩展到 Chrome/Edge 以免预开空白窗引入未知副作用。
 */
function needsTwoStageOpen(): boolean {
  return isIOS() || isSafari() || isFirefox();
}

/** 内置浏览器的 window.open/空白页预开窗容易打断当前 WebView 的异步请求链。 */
function isEmbeddedMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /DingTalk|MicroMessenger|WeChat|wxwork|AlipayClient/i.test(navigator.userAgent);
}

/**
 * 开窗模式解析：将 'auto' 按平台转译为具体模式。
 * - auto + 钉钉/微信等内置浏览器 → 'self'（避免预开空白窗口中断 chat/open）
 * - auto + iOS / Safari / Firefox → 'tab'（两阶段，保留用户手势）
 * - auto + 其他（Chrome/Edge 等）→ null（异步后直接 open，保留原有行为）
 */
function resolveOpenMode(raw: 'auto' | 'tab' | 'self' | 'none' | undefined): 'tab' | 'self' | 'none' | null {
  if (raw === undefined || raw === 'auto') {
    if (isEmbeddedMobileBrowser()) return 'self';
    if (needsTwoStageOpen()) return 'tab';
    return null;
  }
  return raw;
}

/**
 * 主入口：拉 sessionId → 可选预投商品卡 → 打开客服标签页。
 *
 * 严格 popup 浏览器兼容（两阶段开窗）：iOS / Safari / Firefox 在首个 await 之前
 * 同步 window.open('') 建立弹窗（保留用户手势上下文），异步完成后 location.href 导航。
 * 内置浏览器默认走 self，避免预开空白窗打断 fetch。
 *
 * 预投顺序：必须先拿 sessionId（即 groupId），后端 sendMsg 才有 targetGroup。
 * 预投失败不阻塞开窗，宿主可订阅 `presend:error` 决策（如提示用户进会话后重发）。
 */
export async function open(opts: OpenOptions): Promise<void> {
  const cfg = ensureBooted();
  if (!opts.token) throw new Error('DajiCS.open: token is required when using chat/open API');

  // ── 解析 openMode ────────────────────────────────────────────
  const mode = resolveOpenMode(opts.openMode);
  const isReuse = openedWindow != null && !openedWindow.closed;

  // ── 阶段 1（同步）：两阶段开窗的预开窗 ──────────────────────
  // 仅在 mode='tab' 且无复用窗口时执行
  let blankWindow: Window | null = null;
  let blocked = false;

  if (mode === 'tab' && !isReuse) {
    blankWindow = window.open('', WINDOW_NAME);
    if (blankWindow) {
      openedWindow = blankWindow;
      startPolling();
      log('pre-opened blank window for two-stage open');
    } else {
      blocked = true;
      emit('error', {
        source: 'window.open',
        error: new Error('window.open returned null (popup blocked?); falling back to self-navigation'),
      });
    }
  }

  // ── 阶段 2（异步）：获取 sessionId + 可选预投 ────────────────
  let sessionId: string;
  try {
    sessionId = await openWithSession(cfg, opts);
  } catch (e) {
    if (!blocked && blankWindow) cleanupBlankWindow(blankWindow);
    throw e;
  }

  const presend = normalizePresendMessage(opts);
  if (presend) {
    const clientMsgId = buildClientMsgId('preopen', {
      kind: presend.kind,
      data: presend.data,
      userId: opts.userId,
      supplierId: opts.supplierId,
    });
    emitPresendStart(clientMsgId, presend, opts);
    try {
      await preSendCard({
        apiBase: cfg.apiBase,
        groupId: sessionId,
        customType: presend.kind,
        card: presend.data,
        token: opts.token,
        tenant: cfg.tenant,
        source: cfg.source,
        debug: cfg.debug,
        extraHeaders: mergedHeaders(cfg, opts),
      });
      emit('presend:success', { clientMsgId });
    } catch (e) {
      warn('pre-send card failed, opening window anyway:', e);
      emit('presend:error', { clientMsgId, error: e });
    }
  }

  const url = composeBuyerUrl(cfg, opts, sessionId);

  // ── 阶段 3（导航）：按模式执行 ──────────────────────────────
  // 弹窗被拦截 → self 导航
  if (blocked) {
    window.location.href = url;
    return;
  }

  if (mode === 'self') {
    window.location.href = url;
    return;
  }

  if (mode === 'none') {
    cleanupBlankWindow(blankWindow);
    log('openMode=none, emitting url:ready');
    emit('url:ready', { url, sessionId });
    return;
  }

  if (mode === null) {
    // auto + 非严格 popup 浏览器（如 Chrome/Edge）：原有行为——异步完成后直接 openTab
    const win = openTab(url);
    if (win) {
      log('opened tab:', url);
      emit('window:open', { key: WINDOW_NAME, url });
    }
    return;
  }

  // mode === 'tab'
  if (openedWindow && !openedWindow.closed) {
    openedWindow.location.href = url;
    if (isReuse) {
      log('reused existing window, emitting window:focus');
      emit('window:focus', { key: WINDOW_NAME, url });
    }
    log('opened tab:', url);
    emit('window:open', { key: WINDOW_NAME, url });
  } else {
    // 空白窗口已在阶段 2 被关闭（已被轮询检测到并置 null），
    // 此时手势上下文已丢失，不能再用 window.open，直接 self 导航
    window.location.href = url;
    emit('window:open', { key: WINDOW_NAME, url });
  }
}

/** 清理两阶段开窗中预开的空白窗口 */
function cleanupBlankWindow(blankWindow: Window | null): void {
  if (blankWindow && !blankWindow.closed) {
    blankWindow.close();
    stopPolling();
    log('closed pre-opened blank window');
  }
}

/** 兜底：跳过预投，直接打开标签页或按 openMode 跳转。 */
export function openSafe(opts: OpenOptions): void {
  const cfg = ensureBooted();
  const mode = resolveOpenMode(opts.openMode);
  const url = buildTabUrl(cfg, opts);

  if (mode === 'self') {
    window.location.href = url;
    return;
  }

  if (mode === 'none') {
    log('openSafe: openMode=none, emitting url:ready');
    emit('url:ready', { url, sessionId: '' });
    return;
  }

  // mode === 'tab' || mode === null（auto+Chrome/Edge 等 => 原有开窗行为）
  const win = openTab(url);
  if (win) {
    log('opened tab (safe):', url);
    emit('window:open', { key: WINDOW_NAME, url });
  }
}

export { buildQuery, isReadyNow, readyPromise as ready };
export const version = SDK_VERSION;
